import * as R from "ramda";
import React, { useState } from "react";
import { DragSource, DragSourceMonitor } from "react-dnd";
import { FaBars, FaTrash } from "react-icons/fa";
import {
  composeView,
  courseAssessmentViews,
  courseComponentViews,
  courseUnitViews,
} from "../../../../../utils/courseViews";
import { extendView } from "../../../../../utils/courseViewUtils";
import ActionConfirmationButton, { ActionButtonContainer } from "../../../../pages/CourseAuthor/ActionConfirmation";
import EditableTitleLink from "../../../../pages/CourseAuthor/EditableTitleLink";
import {
  DraggableContainer,
  DraggableItem,
  DraggablePlaceholder,
} from "../../../../pages/CourseAuthor/PreviewTab/DraggableContainer.styles";
import { IDropItem } from "../../../../pages/CourseAuthor/PreviewTab/DropTargetFactory";
import { DragAndDropTypes, IUnitTreeProps } from "../../../../pages/CourseAuthor/PreviewTab/types";
import { UnitContentDropDownButton } from "../../../../pages/CourseAuthor/PreviewTab/UnitContentDropdown/DropdownButton";
import { UnitContentDropdown } from "../../../../pages/CourseAuthor/PreviewTab/UnitContentDropdown/index";
import { componentIndex, instructions } from "../../../../pages/CourseAuthor/PreviewTab/util";
import ToggleDisplay from "../../../../utils/ToggleDisplay";
import { findComponent } from "../../../helpers/patches/util";
import { CourseComponentTag, CourseUnitView, DefaultNames, ICourseComponent, ICourseUnit } from "../../../types";
import AddUnitButtonWithDropTarget from "./AddUnitButtonWithDropTarget";
import { ToggleSublistButton } from "./ToggleSublistButton";
import { IUnitListItemCollectedProps, IUnitListItemOwnProps, IUnitListItemProps } from "./types";
import UnitDropTarget from "./UnitDropTarget";
import {
  dragSourceStyle,
  ListItem,
  ListItemButtonsContainer,
  ListItemContainer,
  ListItemTitle,
  SubList,
} from "./UnitList.styles";
import { DropZone, useDragAndDrop } from "../../../../../context/UseDragAndDrop";
import CloneArrows from "../../../../../components/utils/Icons/CloneArrows";
import { TooltipWrapper } from "../../../../../components/pages/Analytics/FilterCriteria/FilterCriteria";
import { onHandleChildDragStart } from "../../../../../utils/dragAndDrop";

export const UnitList: React.FC<IUnitListItemProps> = props => {
  const {
    alternateColor,
    course,
    courseId,
    courseUnit,
    connectDragPreview,
    connectDragSource,
    currentLocale,
    editOperations,
    maxTitleWidth,
    navigationOperations,
    targetUnitView,
    unitPath,
    userId,
    isDraggingReact,
    sourceTabId,
  }: IUnitListItemProps = props;
  const [displayDropdown, setDisplayDropdown] = useState(false);
  const [displaySubtree, setDisplaySubtree] = useState(true);
  const { handleDragging } = useDragAndDrop();

  const toggleDisplay = () => setDisplayDropdown(!displayDropdown);

  const toggleSublist = () => setDisplaySubtree(!displaySubtree);

  const handleDragStart = onHandleChildDragStart(courseUnit, "draggable-item", "unit", handleDragging, sourceTabId);

  const handleDragEnd = () => handleDragging(false);

  const targetUnit = targetUnitView(course);

  if (!targetUnit) {
    return null;
  }

  const instructionalMaterial = instructions(targetUnit);
  const unitAssessmentQuestionsView = composeView(courseAssessmentViews.questions, courseUnitViews.assessment);
  const questions = unitAssessmentQuestionsView(targetUnit) || [];
  const numQuestions: number = questions.filter((q: any) => !q.deleted).length;

  const componentList = courseUnitViews.components(targetUnit) || [];
  const subUnits: ICourseUnit[] = componentList.filter(
    comp => comp.tag === CourseComponentTag.Unit && !comp.deleted
  ) as ICourseUnit[];
  const subUnitPath = [...unitPath, targetUnitView];

  const navigate = (comp: ICourseComponent) => () => {
    navigationOperations.setPath(subUnitPath);
    navigationOperations.setDisplayedComponent(comp)();
  };
  const modifyTitle = (item: ICourseComponent) => editOperations.modifyTitle(unitPath, item);
  const addTitle = (item: ICourseComponent) => editOperations.addTitle(unitPath, item);

  const subListProps: IUnitListItemProps = {
    ...props,
    alternateColor: !alternateColor,
    maxTitleWidth: maxTitleWidth - 2.5,
  };
  const unitUrl = `/course-author/${courseId}?position=${findComponent(course, unitPath, targetUnit)}`;

  return (
    <>
      {connectDragPreview(
        <div>
          <DropZone
            dropPosition={props.dropPosition}
            dropZoneType={"unit"}
            newInstructionType={null}
            editOperations={editOperations}
            path={unitPath}
          >
            <DraggableItem id="draggable-item" isDragging={isDraggingReact}>
              <ListItem style={isDraggingReact ? { visibility: "hidden" } : {}} alternateColor={alternateColor}>
                <ListItemContainer>
                  <ListItemTitle>
                    <ToggleSublistButton display={displaySubtree} onClick={toggleSublist} />
                    <EditableTitleLink
                      addTitle={addTitle(targetUnit)}
                      currentTitle={courseComponentViews.localizedTitle(currentLocale)(targetUnit)}
                      defaultTitle={DefaultNames.Unit}
                      maxWidth={maxTitleWidth}
                      modifyTitle={modifyTitle(targetUnit)}
                      navigate={navigate(targetUnit)}
                      to={unitUrl}
                      isMainTitle={false}
                    />
                  </ListItemTitle>
                  <ListItemButtonsContainer>
                    <UnitContentDropDownButton
                      onClick={toggleDisplay}
                      instructionalMaterial={instructionalMaterial}
                      numQuestions={numQuestions}
                      alternateColor={alternateColor}
                    />
                    <ActionButtonContainer>
                      <ActionConfirmationButton
                        action={"Delete"}
                        actionFn={editOperations.deleteComponent(unitPath, targetUnit)}
                        ActionIcon={FaTrash}
                      />
                    </ActionButtonContainer>
                    {connectDragSource(
                      <div>
                        <TooltipWrapper id="reorder-unit" content="Reorder Units" containerStyle={dragSourceStyle}>
                          <FaBars />
                        </TooltipWrapper>
                      </div>
                    )}
                    <div draggable={true} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                      <TooltipWrapper
                        id="dnd-unit"
                        content="Clone across tabs"
                        containerStyle={{
                          padding: "0.5rem",
                        }}
                      >
                        <CloneArrows />
                      </TooltipWrapper>
                    </div>
                  </ListItemButtonsContainer>
                </ListItemContainer>
              </ListItem>
            </DraggableItem>
          </DropZone>
          <UnitContentDropdown
            course={course}
            courseId={courseId}
            display={displayDropdown}
            editOperations={editOperations}
            locale={currentLocale}
            maxTitleWidth={maxTitleWidth - 2.5}
            navigationOperations={navigationOperations}
            unit={targetUnit}
            unitPath={subUnitPath}
            userId={userId}
            alternateColor={alternateColor}
            unitUrl={unitUrl}
          />
        </div>
      )}
      <ToggleDisplay shouldDisplay={displaySubtree}>
        <SubList>
          {subUnits.map(UnitListItemWithDropTarget(subListProps, subUnitPath, sourceTabId))}
          <AddUnitButtonWithDropTarget
            alternateColor={!alternateColor}
            dropPosition={componentList.length}
            course={course}
            courseId={courseId}
            unitPath={subUnitPath}
            editOperations={editOperations}
          />
        </SubList>
      </ToggleDisplay>
    </>
  );
};

const listItemSource = {
  beginDrag(props: IUnitListItemOwnProps, _: DragSourceMonitor): IDropItem {
    return {
      dropPosition: props.dropPosition,
      item: props.targetUnitView(props.course) as ICourseUnit,
      path: props.unitPath,
      type: null,
    };
  },
};

export const DraggableUnitList = DragSource<IUnitListItemOwnProps, IUnitListItemCollectedProps>(
  DragAndDropTypes.UnitListItem,
  listItemSource,
  (conn, monitor) => ({
    connectDragPreview: conn.dragPreview(),
    connectDragSource: conn.dragSource(),
    isDraggingReact: monitor.isDragging(),
  })
)(UnitList);

export function UnitListItemWithDropTarget(
  props: IUnitTreeProps,
  path: CourseUnitView[],
  sourceTabId: number
): (unit: ICourseUnit, index: number) => React.ReactNode {
  return (unit: ICourseUnit) => {
    const targetUnitView = extendView(props.course, R.last(path), unit);
    const dropPosition = componentIndex(props.course, path, unit);

    if (R.isNil(dropPosition) || dropPosition < 0) {
      return null;
    }

    return (
      <DraggableContainer key={unit.id}>
        <UnitDropTarget
          dropPosition={dropPosition}
          editOperations={props.editOperations}
          path={path}
          type={null}
          alternateColor={!!props.alternateColor}
        />{" "}
        <DraggableUnitList
          alternateColor={!!props.alternateColor}
          course={props.course}
          courseId={props.courseId}
          courseUnit={unit}
          currentLocale={props.currentLocale}
          dropPosition={dropPosition}
          editOperations={props.editOperations}
          maxTitleWidth={props.maxTitleWidth}
          navigationOperations={props.navigationOperations}
          targetUnitView={targetUnitView}
          unitPath={path}
          userId={props.userId}
          sourceTabId={sourceTabId}
        />{" "}
        <DraggablePlaceholder height={4} />
      </DraggableContainer>
    );
  };
}
